import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { shopProducts } from '@/mocks/shop';
import { useCart } from '@/hooks/useCart';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const specOptions: Record<string, string[]> = {
  stationery: ['标准套装', '豪华套装', '迷你套装'],
  books: ['平装版', '精装版'],
  clothing: ['110cm', '120cm', '130cm', '140cm', '150cm', '160cm'],
  digital: ['白色', '黑色', '粉色'],
  desk: ['单人款', '双人款', '豪华款'],
};

const colorMap: Record<string, string> = {
  stationery: '文具套装',
  books: '版本',
  clothing: '尺码',
  digital: '颜色',
  desk: '款式',
};

export default function ShopDetailPage() {
  const { id } = useParams();
  const product = shopProducts.find(p => p.id === id) || shopProducts[0];
  const [selectedSpec, setSelectedSpec] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, totalCount } = useCart();
  const [cartAdded, setCartAdded] = useState(false);
  const navigate = useNavigate();

  const specs = specOptions[product.category] || ['默认规格'];
  const specLabel = colorMap[product.category] || '规格';
  const images = [product.image, product.image, product.image];

  useEffect(() => {
    const schemaId = 'schema-shop-detail';
    let el = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Product',
          '@id': `${SITE_URL}/shop/${product.id}`,
          name: product.name,
          description: `${product.merchant}出品，${product.name}，评分${product.rating}，已售${product.sales.toLocaleString()}件。`,
          image: product.image,
          brand: { '@type': 'Brand', name: product.merchant },
          offers: {
            '@type': 'Offer',
            price: product.price,
            priceCurrency: 'CNY',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/shop/${product.id}`,
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.rating,
            reviewCount: product.sales,
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: '学习用品商城', item: `${SITE_URL}/shop` },
            { '@type': 'ListItem', position: 3, name: product.name, item: `${SITE_URL}/shop/${product.id}` },
          ],
        },
      ],
    };
    el.textContent = JSON.stringify(schema);
    document.title = `${product.name} - ${product.merchant} | 金榜旺财商城`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `${product.name}，${product.merchant}出品，评分${product.rating}，已售${product.sales.toLocaleString()}件，现价¥${product.price}。金榜旺财学习用品商城。`);
    return () => { const s = document.getElementById(schemaId); if (s) s.remove(); };
  }, [product]);

  const handleOrder = () => {
    setOrdering(true);
    setTimeout(() => {
      setOrdering(false);
      setOrderPlaced(true);
      setShowOrderModal(false);
    }, 1800);
  };

  const related = shopProducts.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const reviews = [
    { id: '1', name: '学生家长', rating: 5, content: '质量很好，孩子很喜欢，下次还会购买！', time: '2天前', avatar: 'https://public.readdy.ai/ai/img_res/216886f8d520ffb1d2f7bc13902fffd3.jpg' },
    { id: '2', name: '备考学生', rating: 4, content: '性价比很高，物流也很快，推荐！', time: '5天前', avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20student%20portrait%2C%20simple%20background&width=40&height=40&seq=sr002&orientation=squarish' },
  ];

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />
      <div className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Link to="/" className="hover:text-red-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/shop" className="hover:text-red-600 cursor-pointer">商城</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600 line-clamp-1">{product.name}</span>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 pb-16">
          {orderPlaced && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
              <div>
                <p className="text-green-800 font-semibold text-sm">订单提交成功！</p>
                <p className="text-green-600 text-xs">预计3-5个工作日内发货，感谢您的购买！</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12">
            {/* Images */}
            <div>
              <div className="bg-white rounded-2xl overflow-hidden h-80 mb-3">
                <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${activeImg === i ? 'border-red-500' : 'border-transparent'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs bg-red-50 text-red-600 px-2.5 py-1 rounded-full">{product.merchant}</span>
              </div>
              <h1 className="text-2xl font-black text-gray-900 mb-3">{product.name}</h1>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`ri-star-fill text-sm ${i < Math.floor(product.rating) ? 'text-amber-400' : 'text-gray-200'}`}></i>
                  ))}
                  <span className="text-sm font-semibold text-gray-700 ml-1">{product.rating}</span>
                </div>
                <span className="text-gray-300">|</span>
                <span className="text-xs text-gray-400">已售 {product.sales.toLocaleString()} 件</span>
              </div>

              <div className="bg-red-50 rounded-xl p-4 mb-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-red-600">¥{product.price}</span>
                  <span className="text-gray-400 line-through text-sm">¥{product.originalPrice}</span>
                  <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                    省¥{(product.originalPrice - product.price).toFixed(1)}
                  </span>
                </div>
              </div>

              {/* Spec Selection */}
              <div className="mb-5">
                <p className="text-sm font-medium text-gray-700 mb-2">{specLabel}：<span className="text-red-600">{specs[selectedSpec]}</span></p>
                <div className="flex flex-wrap gap-2">
                  {specs.map((spec, i) => (
                    <button
                      key={spec}
                      onClick={() => setSelectedSpec(i)}
                      className={`px-4 py-2 rounded-xl border-2 text-sm transition-all cursor-pointer whitespace-nowrap ${selectedSpec === i ? 'border-red-500 bg-red-50 text-red-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    >
                      {spec}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-4 mb-6">
                <p className="text-sm font-medium text-gray-700">数量：</p>
                <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 cursor-pointer transition-colors text-gray-600"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center text-sm font-semibold text-gray-900">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => Math.min(99, q + 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 cursor-pointer transition-colors text-gray-600"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <span className="text-xs text-gray-400">库存充足</span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowOrderModal(true)}
                  className="flex-1 py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                >
                  立即购买
                </button>
                <button
                  onClick={() => {
                    for (let i = 0; i < quantity; i++) {
                      addItem({ id: product.id, name: product.name, price: product.price, originalPrice: product.originalPrice, image: product.image, merchant: product.merchant, spec: specs[selectedSpec], category: product.category });
                    }
                    setCartAdded(true);
                  }}
                  className={`flex-1 py-3.5 border-2 font-semibold rounded-full cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-2 ${cartAdded ? 'border-green-500 text-green-600 bg-green-50' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                >
                  <i className={cartAdded ? 'ri-check-line' : 'ri-shopping-cart-line'}></i>
                  {cartAdded ? `已加入 (${totalCount})` : '加入购物车'}
                </button>
              </div>

              {/* Guarantees */}
              <div className="flex flex-wrap gap-4 mt-5">
                {[
                  { icon: 'ri-shield-check-line', text: '正品保障' },
                  { icon: 'ri-truck-line', text: '免费配送' },
                  { icon: 'ri-refresh-line', text: '7天退换' },
                  { icon: 'ri-customer-service-line', text: '专属客服' },
                ].map(g => (
                  <div key={g.text} className="flex items-center gap-1.5 text-xs text-gray-500">
                    <i className={`${g.icon} text-green-500`}></i>
                    {g.text}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Reviews */}
          <div className="mb-12">
            <h2 className="text-xl font-black text-gray-900 mb-5">用户评价</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reviews.map(review => (
                <div key={review.id} className="bg-white rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={review.avatar} alt={review.name} className="w-9 h-9 rounded-full object-cover object-top" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{review.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{review.time}</span>
                  </div>
                  <p className="text-sm text-gray-600">{review.content}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div>
              <h2 className="text-xl font-black text-gray-900 mb-5">同类商品推荐</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {related.map(p => (
                  <Link key={p.id} to={`/shop/${p.id}`} className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-all block" data-product-shop>
                    <div className="h-36 overflow-hidden">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-bold text-gray-900 line-clamp-2 mb-1">{p.name}</p>
                      <p className="text-red-600 font-bold text-sm">¥{p.price}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900">确认订单</h3>
              <button onClick={() => setShowOrderModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex gap-3">
                <img src={product.image} alt={product.name} className="w-14 h-14 rounded-lg object-cover object-top flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 line-clamp-2">{product.name}</p>
                  <p className="text-xs text-gray-400 mt-1">{specLabel}：{specs[selectedSpec]}</p>
                  <p className="text-xs text-gray-400">数量：{quantity}</p>
                </div>
              </div>
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                <span className="text-sm text-gray-600">合计</span>
                <span className="text-lg font-black text-red-600">¥{(product.price * quantity).toFixed(1)}</span>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600 mb-2">收货地址</p>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
                <i className="ri-map-pin-line text-gray-400"></i>
                <span className="text-sm text-gray-500">请添加收货地址</span>
                <i className="ri-arrow-right-s-line text-gray-300 ml-auto"></i>
              </div>
            </div>
            <button
              onClick={handleOrder}
              disabled={ordering}
              className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60"
            >
              {ordering ? '提交中...' : `提交订单 ¥${(product.price * quantity).toFixed(1)}`}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
